// components/Games/ChinesePoker/ChinesePoker.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  startChinesePoker,
  resolveChinesePoker,
  getChinesePokerHistory
} from '../../../services/gameService';
import { Card, Button, Row, Col, Spinner, Alert, Modal } from 'react-bootstrap';
import { FaArrowLeft, FaPlay, FaHistory, FaCheck, FaUndo } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './ChinesePoker.css';

const ChinesePoker = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [betAmount, setBetAmount] = useState(1);
  const [playerCards, setPlayerCards] = useState([]); // [{value,suit,card}]
  const [front, setFront] = useState([]);
  const [middle, setMiddle] = useState([]);
  const [back, setBack] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const getCardImg = (code) =>
    `${process.env.PUBLIC_URL}/images/games/cards/${code}.png`;

  const reset = () => {
    setPlayerCards([]);
    setFront([]);
    setMiddle([]);
    setBack([]);
    setResult(null);
    setError('');
  };

  const start = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await startChinesePoker(betAmount);
      setPlayerCards(data.playerCards || []);
    } catch (e) {
      setError(e?.response?.data?.error || t('chinesePoker.error_start'));
    } finally {
      setLoading(false);
    }
  };

  // Mueve una carta desde/entre zonas
  const toggleTo = (zone, code) => {
    // Elimina la carta de todas las zonas
    setFront((prev) => prev.filter((c) => c !== code));
    setMiddle((prev) => prev.filter((c) => c !== code));
    setBack((prev) => prev.filter((c) => c !== code));

    // Añade si hay hueco
    if (zone === 'front' && front.length < 3) setFront((p) => [...p, code]);
    if (zone === 'middle' && middle.length < 5) setMiddle((p) => [...p, code]);
    if (zone === 'back' && back.length < 5) setBack((p) => [...p, code]);
  };

  const available = () => {
    const used = new Set([...front, ...middle, ...back]);
    return playerCards.map((c) => c.card).filter((code) => !used.has(code));
  };

  const canResolve =
    front.length === 3 && middle.length === 5 && back.length === 5;

  const resolve = async () => {
    if (!canResolve) {
      setError(t('chinesePoker.complete_zones'));
      return;
    }
    setResolving(true);
    setError('');
    try {
      const data = await resolveChinesePoker({ front, middle, back });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.error || t('chinesePoker.error_resolve'));
    } finally {
      setResolving(false);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await getChinesePokerHistory();
      setHistory(rows || []);
      setShowHistory(true);
    } catch (e) {
      setError(t('chinesePoker.error_history'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-container">
      <Card className="cp-card">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/sessions')}
              aria-label={t('common.back', { defaultValue: 'Back' })}
              title={t('common.back', { defaultValue: 'Back' })}
            >
              <FaArrowLeft className="me-2" />
              {t('common.back', { defaultValue: 'Back' })}
            </Button>
            <h2 className="m-0">{t('chinesePoker.title')}</h2>
            <div />
          </div>

          <p className="mb-2 text-muted">
            {t('chinesePoker.arrange_hint')}
          </p>

          <div className="d-flex gap-2 align-items-center mb-3">
            <input
              type="number"
              min="1"
              max="1000000"
              value={betAmount}
              onChange={(e) => setBetAmount(Number(e.target.value))}
              className="form-control"
              style={{ maxWidth: 180 }}
            />
            <span className="fw-bold">MENEL</span>
            <Button onClick={start} disabled={loading || playerCards.length > 0}>
              {loading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <FaPlay className="me-2" />
                  {t('chinesePoker.play')}
                </>
              )}
            </Button>
            <Button variant="outline-info" onClick={fetchHistory} disabled={loading}>
              <FaHistory className="me-2" />
              {t('chinesePoker.history')}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={reset}
              disabled={loading && !playerCards.length}
            >
              <FaUndo className="me-2" />
              {t('chinesePoker.reset')}
            </Button>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {/* Cartas disponibles */}
          {playerCards.length > 0 && (
            <>
              <h5>{t('chinesePoker.your_cards')}</h5>
              <div className="cp-pool">
                {available().map((code) => (
                  <img
                    key={code}
                    src={getCardImg(code)}
                    alt={code}
                    className="cp-card-img"
                    onClick={() => toggleTo('front', code)}
                    title={t('chinesePoker.front_label')}
                  />
                ))}
              </div>

              <Row className="mt-3 g-3">
                <Col md={4}>
                  <div className="cp-zone">
                    <div className="cp-zone-header">
                      {t('chinesePoker.front_label')}
                    </div>
                    <div className="cp-zone-body">
                      {front.map((code) => (
                        <img
                          key={code}
                          src={getCardImg(code)}
                          alt={code}
                          className="cp-card-img"
                          onClick={() => toggleTo('middle', code)}
                          title={t('chinesePoker.middle_label')}
                        />
                      ))}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="cp-zone">
                    <div className="cp-zone-header">
                      {t('chinesePoker.middle_label')}
                    </div>
                    <div className="cp-zone-body">
                      {middle.map((code) => (
                        <img
                          key={code}
                          src={getCardImg(code)}
                          alt={code}
                          className="cp-card-img"
                          onClick={() => toggleTo('back', code)}
                          title={t('chinesePoker.back_label')}
                        />
                      ))}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="cp-zone">
                    <div className="cp-zone-header">
                      {t('chinesePoker.back_label')}
                    </div>
                    <div className="cp-zone-body">
                      {back.map((code) => (
                        <img
                          key={code}
                          src={getCardImg(code)}
                          alt={code}
                          className="cp-card-img"
                          onClick={() => toggleTo('front', code)}
                          title={t('chinesePoker.front_label')}
                        />
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-center mt-3">
                <Button
                  variant="success"
                  onClick={resolve}
                  disabled={!canResolve || resolving}
                >
                  {resolving ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <FaCheck className="me-2" />
                      {t('chinesePoker.resolve')}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}

          {/* Resultado */}
          {result && (
            <div className="mt-4">
              <h5>{t('chinesePoker.result_title')}</h5>
              {result.foul ? (
                <Alert variant="warning">{t('chinesePoker.foul')}</Alert>
              ) : (
                <>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="cp-result-block">
                        <div className="cp-zone-header">
                          {t('chinesePoker.your_cards')}
                        </div>
                        <div className="cp-line">
                          Front: {result.player.front.join(', ')}
                        </div>
                        <div className="cp-line">
                          Middle: {result.player.middle.join(', ')}
                        </div>
                        <div className="cp-line">
                          Back: {result.player.back.join(', ')}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="cp-result-block">
                        <div className="cp-zone-header">Dealer</div>
                        <div className="cp-line">
                          Front: {result.dealer.front.join(', ')}
                        </div>
                        <div className="cp-line">
                          Middle: {result.dealer.middle.join(', ')}
                        </div>
                        <div className="cp-line">
                          Back: {result.dealer.back.join(', ')}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <div className="mt-3">
                    <div>
                      {t('chinesePoker.points')}: <strong>{result.points}</strong>
                    </div>
                    <div>
                      {t('chinesePoker.winnings')}:{' '}
                      <strong>{result.winnings}</strong>
                    </div>
                    <div>
                      {t('chinesePoker.new_balance')}:{' '}
                      <strong>{result.newBalance}</strong>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showHistory} onHide={() => setShowHistory(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('chinesePoker.history')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 420, overflowY: 'auto' }}>
          {history.length ? (
            <ul className="list-group">
              {history.map((h, i) => (
                <li key={i} className="list-group-item">
                  <strong>{t('chinesePoker.result')}:</strong> {h.result}
                  <br />
                  <strong>{t('chinesePoker.details')}:</strong> {h.details}
                  <br />
                  <strong>{t('chinesePoker.date')}:</strong>{' '}
                  {new Date(h.created_at).toLocaleString()}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-muted">{t('chinesePoker.no_history')}</div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ChinesePoker;
